import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';
import Hero from '@/components/Hero';
import SolutionsSection from '@/components/SolutionsSection';
import OwletSection from '@/components/OwletSection';
import ShowcaseSection from '@/components/ShowcaseSection';
import TestimonialsSection from '@/components/TestimonialsSection';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import FaqSection from '@/components/marketing/FaqSection';
import '@/components/marketing/marketing.css';
import { useUserPreferences } from '@/store/useStore';
import { ACCENT, ACCENT_BRIGHT } from '@/styles/premium';

const marketingBrandTokens = {
  '--mk-brand': ACCENT,
  '--mk-brand-bright': ACCENT_BRIGHT,
} as React.CSSProperties;

export default function LandingPage() {
  const navigate = useNavigate();
  const userPreferences = useUserPreferences();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  // Auto-redirect if user is already logged in
  useEffect(() => {
    if (userPreferences && !isRedirecting) {
      console.log('✅ User preferences found, redirecting to dashboard');
      setIsRedirecting(true);
      navigate('/dashboard/home', { replace: true });
    }
  }, [userPreferences, navigate, isRedirecting]);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keep hooks unconditional when the profile triggers a redirect.
  if (userPreferences || isRedirecting) {
    return null;
  }

  return (
    <div className="marketing-page min-h-screen overflow-x-hidden relative" style={{ background: '#0A0A0A' }}>
      <div className="relative z-10">
        {/* Glassy Modern Navbar */}
        <nav className="fixed top-6 left-1/2 -translate-x-1/2 w-[90%] max-w-6xl z-50">
        <div 
          className={`backdrop-blur-xl rounded-2xl px-6 py-4 transition-all duration-300 ${
            isScrolled ? 'border border-[#14B8A6]/30' : 'border border-transparent'
          }`}
          style={{
            // Over the hero the nav is always on dark pixels; once scrolled it
            // sits on the theme-following sections below.
            '--nav-fg': isScrolled ? 'var(--mk-text)' : '#fff',
            background: isScrolled ? 'var(--mk-nav-bg)' : 'transparent',
          } as React.CSSProperties}
        >
          <div className="flex justify-between items-center">
            {/* Logo - Left */}
            <Logo size="md" clickable={true} navigateTo="/" scheduleWhite={true} />

            {/* Nav Links - Center */}
            <div className="hidden lg:flex items-center space-x-8">
              <a 
                href="#solutions" 
                className="text-[15px] font-medium transition-all duration-300 relative group text-[color:var(--nav-fg)] hover:text-[#14B8A6]"
              >
                Solutions
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#14B8A6] group-hover:w-full transition-all duration-300"></span>
              </a>
              <a 
                href="#owlet" 
                className="text-[15px] font-medium transition-all duration-300 relative group text-[color:var(--nav-fg)] hover:text-[#14B8A6]"
              >
                Owlet AI
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#14B8A6] group-hover:w-full transition-all duration-300"></span>
              </a>
              <a 
                href="#testimonials" 
                className="text-[15px] font-medium transition-all duration-300 relative group text-[color:var(--nav-fg)] hover:text-[#14B8A6]"
              >
                Testimonials
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#14B8A6] group-hover:w-full transition-all duration-300"></span>
              </a>
              <button 
                onClick={() => navigate('/about')} 
                className="text-[15px] font-medium transition-all duration-300 relative group text-[color:var(--nav-fg)] hover:text-[#14B8A6]"
              >
                About
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#14B8A6] group-hover:w-full transition-all duration-300"></span>
              </button>
              <button 
                onClick={() => navigate('/contact')} 
                className="text-[15px] font-medium transition-all duration-300 relative group text-[color:var(--nav-fg)] hover:text-[#14B8A6]"
              >
                Contact
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#14B8A6] group-hover:w-full transition-all duration-300"></span>
              </button>
            </div>

            {/* CTA Button - Right */}
            <div className="hidden lg:flex items-center">
              <Button 
                size="sm"
                variant="outline"
                style={{
                  borderColor: 'rgba(0, 212, 200, 0.5)',
                  color: '#00d4c8'
                }}
                className="h-10 px-6 font-bold tracking-wide rounded-none hover:bg-primary/10 transition-all backdrop-blur-md"
                onClick={() => navigate('/student-form')}
              >
                Get Started
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={mobileMenuOpen}
              aria-controls="marketing-mobile-menu"
              className="lg:hidden p-2 transition-colors duration-300 text-[color:var(--nav-fg)]"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div id="marketing-mobile-menu" className="lg:hidden mt-4 pt-4 max-h-[calc(100dvh-130px)] overflow-y-auto overscroll-contain border-t border-gray-200/40 animate-slide-down">
              <div className="space-y-3">
                <a 
                  href="#solutions" 
                  className="block py-2 text-[15px] font-medium transition-colors text-[color:var(--nav-fg)] hover:text-[#14B8A6]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Solutions
                </a>
                <a 
                  href="#owlet" 
                  className="block py-2 text-[15px] font-medium transition-colors text-[color:var(--nav-fg)] hover:text-[#14B8A6]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Owlet AI
                </a>
                <a 
                  href="#testimonials" 
                  className="block py-2 text-[15px] font-medium transition-colors text-[color:var(--nav-fg)] hover:text-[#14B8A6]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Testimonials
                </a>
                <button 
                  onClick={() => { navigate('/about'); setMobileMenuOpen(false); }} 
                  className="block w-full text-left py-2 text-[15px] font-medium transition-colors text-[color:var(--nav-fg)] hover:text-[#14B8A6]"
                >
                  About
                </button>
                <button 
                  onClick={() => { navigate('/contact'); setMobileMenuOpen(false); }} 
                  className="block w-full text-left py-2 text-[15px] font-medium transition-colors text-[color:var(--nav-fg)] hover:text-[#14B8A6]"
                >
                  Contact
                </button>
                <Button
                  variant="outline"
                  style={{
                    borderColor: 'rgba(0, 212, 200, 0.5)',
                    color: '#00d4c8'
                  }}
                  className="w-full h-12 font-bold tracking-wide rounded-none hover:bg-primary/10 transition-all backdrop-blur-md"
                  onClick={() => { navigate('/student-form'); setMobileMenuOpen(false); }}
                >
                  Get Started
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <main>
        <Hero />
        <div className="marketing-content" style={marketingBrandTokens}>
          <SolutionsSection />
          <div id="owlet"><OwletSection /></div>
          <ShowcaseSection />
          <div id="testimonials"><TestimonialsSection /></div>
          <FaqSection />
        </div>
      </main>
      <div className="marketing-content" style={marketingBrandTokens}><MarketingFooter /></div>
      </div>
    </div>
  );
}
